import { makeAutoObservable } from 'mobx'
import { showToast, type IDropdownItem } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { VoiceServicesApi } from 'entities/Compliance/api/voiceServicesApi'
import { type IResponseVoiceIntegrityUseCase } from 'entities/Compliance/api/types/voiceServicesTypes'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { CallingRoutesSteps } from 'pages/settings/pages/compliance/pages/calling/route/type'
import { type CallingStepsStore } from './callingStepsStore'

const MAX_COMPANY_SIZE = 1_000_000
const MAX_CALLS_PER_DAY = 1_000_000
const ERROR_MESSAGE = 'Please enter a number between 1 and 1,000,000'

export class VoiceIntegrityStore {
  constructor(private _callingStepsStore: CallingStepsStore, private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  private _useCases: IResponseVoiceIntegrityUseCase[] = []

  private _useCaseValue: string | null = null
  private _companySize = ''
  private _callsPerDay = ''
  private _notes = ''
  private _companySizeError = ''
  private _callsPerDayError = ''

  get callingStepsStore() {
    return this._callingStepsStore
  }

  get useCases() {
    return this._useCases
  }

  get activeUseCase() {
    return this.useCases.find((useCase) => useCase.value === this.useCaseValue)
  }

  get activeUseCaseTitle() {
    return this.activeUseCase?.title
  }

  get activeUseCaseValue() {
    return this.activeUseCase?.value
  }

  get useCaseValue() {
    return this._useCaseValue
  }

  get useCaseAsItems(): IDropdownItem[] {
    return this.useCases
      .map((useCase) => ({
        id: useCase.value,
        label: useCase.title || '',
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }

  get companySize() {
    return this._companySize
  }

  get callsPerDay() {
    return this._callsPerDay
  }

  get notes() {
    return this._notes
  }

  get companySizeError() {
    return this._companySizeError
  }

  get callsPerDayError() {
    return this._callsPerDayError
  }

  get isSubmitDisabled() {
    return (
      !this.useCaseValue ||
      !this.companySize.length ||
      !this.callsPerDay.length ||
      this.companySizeError.length > 0 ||
      this.callsPerDayError.length > 0
    )
  }

  get errorMessage() {
    const { voiceIntegrity } = this._entities.complianceStore.complianceVoiceServicesStore

    if (voiceIntegrity && voiceIntegrity.registration_errors) {
      return voiceIntegrity.registration_errors[0].message
    }

    return null
  }
  movetoVoiceIntegritySecondStep = () => {
    this.callingStepsStore.stepsStore.setStep(2)

    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.calling.root}/${CallingRoutesSteps.companyInfo}`,
    })
  }

  setUseCase = (useCase: string) => {
    this._useCaseValue = useCase
  }

  loadUseCases = async () => {
    try {
      const { data } = await VoiceServicesApi.getVoiceIntegrityUseCases()

      this._useCases = data
    } catch (e) {
      logger.error(e)
    }
  }

  setCompanySize = (companySize: string) => {
    if (companySize === '' || /^[1-9]\d*$/.test(companySize)) {
      this._companySize = companySize
      this._companySizeError = ''

      const numValue = parseInt(companySize, 10)

      if (numValue > MAX_COMPANY_SIZE) {
        this._companySizeError = ERROR_MESSAGE
      }
    }
  }

  setCallsPerDay = (callsPerDay: string) => {
    if (callsPerDay === '' || /^[1-9]\d*$/.test(callsPerDay)) {
      this._callsPerDay = callsPerDay
      this._callsPerDayError = ''

      const numValue = parseInt(callsPerDay, 10)

      if (numValue > MAX_CALLS_PER_DAY) {
        this._callsPerDayError = ERROR_MESSAGE
      }
    }
  }

  setNotes = (notes: string) => {
    this._notes = notes
  }

  checkCompletedVoiceIntegrity = () => {
    const { voiceIntegrity } = this._entities.complianceStore.complianceVoiceServicesStore

    if (voiceIntegrity) {
      this.setUseCase(voiceIntegrity.use_case)
      this.setCompanySize(String(voiceIntegrity.business_employee_count))
      this.setCallsPerDay(String(voiceIntegrity.average_business_day_call_volume))
      this.setNotes(voiceIntegrity.notes)
    }
  }

  submitVoiceIntegrity = async () => {
    if (this.callingStepsStore.complianceLayerStore.preventSubmitModal()) {
      return
    }

    try {
      this.callingStepsStore.stepsStore.setStepLoading(true)

      const payload = {
        average_business_day_call_volume: this.callsPerDay,
        business_employee_count: this.companySize,
        notes: this.notes,
        use_case: this.activeUseCase?.title || '',
        email: this._entities.organizationStore.owner?.email || '',
      }

      const { voiceIntegrity } = this._entities.complianceStore.complianceVoiceServicesStore
      let response

      if (voiceIntegrity?.id) {
        response = await VoiceServicesApi.updateVoiceIntegrity(payload, voiceIntegrity.id)
      } else {
        response = await VoiceServicesApi.submitVoiceIntegrity(payload)
      }

      const { data } = response

      if (data) {
        this._entities.complianceStore.complianceVoiceServicesStore.setVoiceIntegrity(data)

        uiStore.changeRoute({
          path: `/${SettingsPath.compliance.calling.root}`,
        })

        showToast({
          type: 'info',
          title: 'Voice Integrity submitted for review',
        })
      }
    } catch (e) {
      logger.error(e)
    } finally {
      this.callingStepsStore.stepsStore.setStepLoading(false)
    }
  }
}
